import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "../../components/Header";
import api from "../../utils/api";
import { getImageUrl } from "../../utils/imageUtils";
import {
  toLocalizedNumber,
  toLocalizedPrice,
  toLocalizedStatus,
} from "../../utils/localeUtils";
import {
  FaBox,
  FaShoppingCart,
  FaClipboardList,
  FaUser,
  FaComments,
  FaShoppingBasket,
  FaMapMarkerAlt,
  FaMoneyBillWave,
  FaStar,
  FaArrowRight,
  FaClock,
  FaCheckCircle,
  FaCheckDouble,
  FaTruck,
  FaHourglassHalf,
  FaHandHolding,
} from "react-icons/fa";
import "./ConsumerDashboard.css";

const ConsumerDashboard = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalOrders: 0,
    pendingOrders: 0,
    completedOrders: 0,
    totalSpent: 0,
    unreadMessages: 0,
  });
  const [recentOrders, setRecentOrders] = useState([]);

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      try {
        const userObj = JSON.parse(userData);
        setUser(userObj);
        if (userObj.role !== "consumer") {
          navigate("/");
          return;
        }
        fetchDashboardData();
      } catch (e) {
        setLoading(false);
      }
    } else {
      navigate("/login");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const fetchDashboardData = async () => {
    try {
      const ordersRes = await api.get("/orders");
      const orders = ordersRes.data || [];

      let unreadMessages = 0;
      try {
        const convRes = await api.get("/messages/conversations");
        unreadMessages = (convRes.data || []).reduce(
          (s, c) => s + (c.unreadCount || 0),
          0,
        );
      } catch (e) {
        /* ignore */
      }

      const totalSpent = orders
        .filter(
          (o) =>
            ["delivered", "received", "completed"].includes(o.orderStatus) ||
            o.paymentStatus === "paid",
        )
        .reduce((s, o) => s + (o.totalPrice || 0), 0);

      setStats({
        totalOrders: orders.length,
        pendingOrders: orders.filter((o) =>
          ["pending", "confirmed", "processing", "shipped"].includes(
            o.orderStatus,
          ),
        ).length,
        completedOrders: orders.filter((o) =>
          ["delivered", "received", "completed"].includes(o.orderStatus),
        ).length,
        totalSpent,
        unreadMessages,
      });

      const sorted = [...orders].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
      );
      setRecentOrders(sorted.slice(0, 4));
      setLoading(false);
    } catch (e) {
      console.error("Error fetching dashboard data:", e);
      setLoading(false);
    }
  };

  const getTimeAgo = (date) => {
    const diff = Math.floor((Date.now() - new Date(date)) / 1000);
    if (diff < 60) return t("consumerDashboard.justNow");
    if (diff < 3600)
      return t("consumerDashboard.minutesAgo", {
        minutes: Math.floor(diff / 60),
      });
    if (diff < 86400)
      return t("consumerDashboard.hoursAgo", {
        hours: Math.floor(diff / 3600),
      });
    return t("consumerDashboard.daysAgo", { days: Math.floor(diff / 86400) });
  };

  const statusMeta = {
    pending: { color: "#f59e0b", bg: "#fffbeb", icon: <FaHourglassHalf /> },
    confirmed: { color: "#3b82f6", bg: "#eff6ff", icon: <FaCheckCircle /> },
    processing: { color: "#8b5cf6", bg: "#f5f3ff", icon: <FaClock /> },
    shipped: { color: "#06b6d4", bg: "#ecfeff", icon: <FaTruck /> },
    delivered: { color: "#22c55e", bg: "#f0fdf4", icon: <FaCheckCircle /> },
    received: { color: "#0d9488", bg: "#f0fdfa", icon: <FaHandHolding /> },
    completed: { color: "#15803d", bg: "#ecfdf5", icon: <FaCheckDouble /> },
    cancelled: { color: "#ef4444", bg: "#fef2f2", icon: <FaBox /> },
  };

  const quickActions = [
    {
      to: "/market",
      icon: <FaShoppingCart />,
      label: t("consumerDashboard.shopNow"),
      accent: "#4CAF50",
    },
    {
      to: "/orders",
      icon: <FaClipboardList />,
      label: t("consumerDashboard.myOrders"),
      accent: "#2196F3",
    },
    {
      to: "/consumer/profile",
      icon: <FaUser />,
      label: t("consumerDashboard.myProfile"),
      accent: "#9C27B0",
    },
    {
      to: "/messages",
      icon: <FaComments />,
      label: t("consumerDashboard.messages"),
      accent: "#FF9800",
      badge: stats.unreadMessages,
    },
    {
      to: "/consumer/payments",
      icon: <FaMoneyBillWave />,
      label: t("consumerDashboard.paymentHistory"),
      accent: "#00BCD4",
    },
    {
      to: "/consumer/reviews",
      icon: <FaStar />,
      label: t("consumerDashboard.myReviews"),
      accent: "#FFC107",
    },
    {
      to: "/cart",
      icon: <FaShoppingBasket />,
      label: t("consumerDashboard.shoppingCart"),
      accent: "#E91E63",
    },
    {
      to: "/nearby",
      icon: <FaMapMarkerAlt />,
      label: t("consumerDashboard.nearbyFarmers"),
      accent: "#607D8B",
    },
  ];

  if (loading) {
    return (
      <div className="cd-page">
        <Header />
        <div className="cd-loader">
          <div className="cd-loader-dots">
            <span />
            <span />
            <span />
          </div>
          <p>{t("consumerDashboard.loading")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="cd-page">
      <Header />

      <div className="cd-container">
        {/* Greeting */}
        <section className="cd-greeting">
          <div className="cd-greeting-text">
            <h1>
              {t("consumerDashboard.welcomeBack", {
                name: user?.name?.split(" ")[0] || "User",
              })}
            </h1>
            <p>{t("consumerDashboard.subtitle")}</p>
          </div>
          <Link to="/market" className="cd-shop-btn">
            <FaShoppingCart /> {t("consumerDashboard.shopNow")}
          </Link>
        </section>

        {/* Stats Strip */}
        <section className="cd-stats">
          <div className="cd-stat cd-stat--orders">
            <div className="cd-stat-icon">
              <FaBox />
            </div>
            <div className="cd-stat-data">
              <span className="cd-stat-num">
                {toLocalizedNumber(stats.totalOrders)}
              </span>
              <span className="cd-stat-label">
                {t("consumerDashboard.totalOrders")}
              </span>
            </div>
          </div>
          <div className="cd-stat cd-stat--active">
            <div className="cd-stat-icon">
              <FaClock />
            </div>
            <div className="cd-stat-data">
              <span className="cd-stat-num">
                {toLocalizedNumber(stats.pendingOrders)}
              </span>
              <span className="cd-stat-label">
                {t("consumerDashboard.activeOrders")}
              </span>
            </div>
          </div>
          <div className="cd-stat cd-stat--done">
            <div className="cd-stat-icon">
              <FaCheckCircle />
            </div>
            <div className="cd-stat-data">
              <span className="cd-stat-num">
                {toLocalizedNumber(stats.completedOrders)}
              </span>
              <span className="cd-stat-label">
                {t("consumerDashboard.completed")}
              </span>
            </div>
          </div>
          <div className="cd-stat cd-stat--spent">
            <div className="cd-stat-icon">
              <FaMoneyBillWave />
            </div>
            <div className="cd-stat-data">
              <span className="cd-stat-num">
                Rs. {toLocalizedPrice(stats.totalSpent)}
              </span>
              <span className="cd-stat-label">
                {t("consumerDashboard.totalSpent")}
              </span>
            </div>
          </div>
        </section>

        {/* Quick Actions */}
        <section className="cd-actions-section">
          <h2 className="cd-section-title">
            {t("consumerDashboard.quickActions")}
          </h2>
          <div className="cd-actions">
            {quickActions.map((a, i) => (
              <Link
                key={i}
                to={a.to}
                className="cd-action"
                style={{ "--accent": a.accent }}
              >
                <div className="cd-action-icon">{a.icon}</div>
                <span className="cd-action-label">{a.label}</span>
                {a.badge > 0 && (
                  <span className="cd-action-badge">
                    {toLocalizedNumber(a.badge)}
                  </span>
                )}
              </Link>
            ))}
          </div>
        </section>

        {/* Recent Orders */}
        <section className="cd-orders-section">
          <div className="cd-section-head">
            <h2 className="cd-section-title">
              {t("consumerDashboard.recentOrders")}
            </h2>
            <Link to="/orders" className="cd-view-all">
              {t("consumerDashboard.viewAll")} <FaArrowRight />
            </Link>
          </div>

          {recentOrders.length > 0 ? (
            <div className="cd-orders">
              {recentOrders.map((order) => {
                const meta =
                  statusMeta[order.orderStatus] || statusMeta.pending;
                return (
                  <Link
                    to={`/order/${order._id}`}
                    key={order._id}
                    className="cd-order"
                  >
                    <div
                      className="cd-order-status-strip"
                      style={{ background: meta.color }}
                    />
                    {(() => {
                      const firstItem = order.items?.[0];
                      const imgSrc = firstItem?.product?.images?.[0]
                        ? firstItem.product.images[0].startsWith("http")
                          ? firstItem.product.images[0]
                          : getImageUrl(firstItem.product.images[0])
                        : "/images/deafultpic.png";
                      const itemName =
                        firstItem?.name ||
                        firstItem?.product?.name ||
                        "Product";
                      return (
                        <div className="cd-order-thumb">
                          <img
                            src={imgSrc}
                            alt={itemName}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = "/images/deafultpic.png";
                            }}
                          />
                        </div>
                      );
                    })()}
                    <div className="cd-order-body">
                      <div className="cd-order-top">
                        <span className="cd-order-name">
                          {order.items?.[0]?.name ||
                            order.items?.[0]?.product?.name ||
                            "Product"}
                          {(order.items?.length || 0) > 1 && (
                            <span className="cd-order-more">
                              {" "}
                              +
                              {toLocalizedNumber(
                                (order.items?.length || 1) - 1,
                              )}
                            </span>
                          )}
                        </span>
                        <span
                          className="cd-order-badge"
                          style={{ color: meta.color, background: meta.bg }}
                        >
                          {meta.icon} {toLocalizedStatus(order.orderStatus, t)}
                        </span>
                      </div>
                      <div className="cd-order-bottom">
                        <span className="cd-order-price">
                          Rs. {toLocalizedPrice(order.totalPrice || 0)}
                        </span>
                        <span className="cd-order-time">
                          {getTimeAgo(order.createdAt)}
                        </span>
                      </div>
                    </div>
                    <FaArrowRight className="cd-order-arrow" />
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="cd-empty">
              <FaShoppingCart className="cd-empty-icon" />
              <p>{t("consumerDashboard.noOrdersYet")}</p>
              <Link to="/market" className="cd-btn-primary">
                {t("consumerDashboard.startShopping")}
              </Link>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default ConsumerDashboard;
